import { Input } from 'common/ui/input';
import React, { useEffect, useState } from 'react';

interface DecimalInputProps extends Omit<React.ComponentProps<typeof Input>, 'onChange' | 'value'> {
  value?: number | undefined;
  onChange?: (value: number | undefined) => void;
  placeholder?: string;
  allowNegative?: boolean;
  maxDecimals?: number;
}

// Custom function to format decimal numbers with thousand separators
const formatDecimalWithSeparators = (value: number, maxDecimals: number = 2): string => {
  // Format to fixed decimal places first
  const fixedValue = value.toFixed(maxDecimals);
  
  // Split into integer and decimal parts
  const parts = fixedValue.split('.');
  const integerPart = parts[0];
  const decimalPart = parts[1];
  
  // Add thousand separators to integer part
  const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  
  // Combine with decimal part
  return decimalPart ? `${formattedInteger}.${decimalPart}` : formattedInteger;
};

const DecimalInput = React.forwardRef<HTMLInputElement, DecimalInputProps>(
  ({ value, onChange, placeholder, allowNegative = false, maxDecimals = 2, ...props }, ref) => {
    const [displayValue, setDisplayValue] = useState<string>('');
    const [isEditing, setIsEditing] = useState<boolean>(false);

    // Update display value when prop value changes (only when not editing)
    useEffect(() => {
      if (!isEditing) {
        if (value === undefined || value === null || value === 0) {
          setDisplayValue('');
        } else {
          // Format the decimal number with thousand separators
          const formatted = formatDecimalWithSeparators(value, maxDecimals);
          setDisplayValue(formatted);
        }
      }
    }, [value, maxDecimals, isEditing]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      setIsEditing(true);
      const inputValue = e.target.value;
      
      // Remove all non-numeric characters except decimal point and minus sign
      let cleanedValue = inputValue.replace(/[^\d.-]/g, '');
      
      // Handle negative numbers
      if (!allowNegative) {
        cleanedValue = cleanedValue.replace(/-/g, '');
      }
      
      // Ensure only one decimal point
      const parts = cleanedValue.split('.');
      if (parts.length > 2) {
        cleanedValue = parts[0] + '.' + parts.slice(1).join('');
      }
      
      // Update display value - don't format during typing, just show what user types
      if (cleanedValue === '' || cleanedValue === '-') {
        setDisplayValue(cleanedValue);
        onChange?.(undefined);
        return;
      }
      
      // Parse the cleaned value to a number
      const numValue = parseFloat(cleanedValue);
      
      if (!isNaN(numValue)) {
        // During typing, just show the cleaned value without formatting
        setDisplayValue(cleanedValue);
        onChange?.(numValue);
      } else {
        setDisplayValue(cleanedValue);
        onChange?.(undefined);
      }
    };

    const handleBlur = () => {
      setIsEditing(false);
      // On blur, format properly and ensure correct decimal places
      if (displayValue === '' || displayValue === '-') {
        setDisplayValue('');
        onChange?.(undefined);
      } else {
        // Remove commas and parse
        const numValue = parseFloat(displayValue.replace(/,/g, ''));
        if (!isNaN(numValue)) {
          // Format properly on blur with thousand separators and correct decimal places
          const formatted = formatDecimalWithSeparators(numValue, maxDecimals);
          setDisplayValue(formatted);
          onChange?.(numValue);
        } else {
          setDisplayValue('');
          onChange?.(undefined);
        }
      }
    };

    const handleFocus = () => {
      setIsEditing(true);
    };

    return (
      <Input
        ref={ref}
        type="text"
        value={displayValue}
        onChange={handleChange}
        onBlur={handleBlur}
        onFocus={handleFocus}
        placeholder={placeholder}
        {...props}
      />
    );
  }
);

DecimalInput.displayName = 'DecimalInput';

export default DecimalInput; 